import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { developmentBundleId } from '@omnea-app/frontend/config/jsBundleInvalidation'
import { LatestBuildIdResponse } from '@omnea-app/frontend/pages/api/latest-build-id'
import { onFetchFailErrors } from '@omnea-app/frontend/utils/error-handling/fetchFailHandling'
import { handleInternalAPIResponse } from 'utils/general/observability/handleInternalAPIResponse'
import { ServiceUrls } from 'utils/general/serviceUrls'

const currentBundleId = process.env.NEXT_PUBLIC_BUNDLE_ID || developmentBundleId

const useJsBundleInvalidation_production = () => {
  const router = useRouter()

  const [latestBundleId, setLatestBundleId] = useState(currentBundleId)
  const lastFetchTimestampRef = useRef(0)
  const isFetchingRef = useRef(false)

  useEffect(() => {
    // Check for a new bundle version, but only once per minute at max
    const fetchLatestBundleId = async () => {
      if (isFetchingRef.current) return

      const currentTimeMs = Date.now()
      const timeSinceLastFetchMs = currentTimeMs - lastFetchTimestampRef.current

      const oneMinuteMs = 1000 * 60
      if (timeSinceLastFetchMs < oneMinuteMs) return

      isFetchingRef.current = true

      const response = await fetch(`${ServiceUrls.NEXT_PUBLIC}/api/latest-build-id`)
        .then(handleInternalAPIResponse<LatestBuildIdResponse>)
        .then((response) => ('id' in response ? response : new Error('No bundle ID in the response')))
        .catch((error: Error) => error)

      if (response instanceof Error) {
        onFetchFailErrors('Failed to fetch latest bundle ID', response)
      } else {
        setLatestBundleId(response.id)
      }

      isFetchingRef.current = false
      lastFetchTimestampRef.current = Date.now()
    }

    router.events.on('routeChangeStart', fetchLatestBundleId) // Subscribe
    return () => router.events.off('routeChangeStart', fetchLatestBundleId) // Unsubscribe
  }, [router.events])

  useEffect(() => {
    // If the bundle hasn't changed, do nothing
    if (latestBundleId === currentBundleId) return

    console.info(`New bundle detected: ${latestBundleId} (current: ${currentBundleId}). App will reload on path change.`)

    const reload = (newPathname: string) => {
      const currentTopLevelPath = getTopLevelPath(window.location.pathname)
      const nextTopLevelPath = getTopLevelPath(newPathname)

      /**
       * We only want to reload if going from one top-level path to another
       * This prevents a whole-site reloading if just changing tabs on a table.
       *
       * e.g.
       * ✅ from "/requests/[tab]" to "/workflows"
       * ❌ from "/renewals/pending" to "/renewals/completed"
       */
      if (currentTopLevelPath === nextTopLevelPath) return

      const newUrl = `${process.env.NEXT_PUBLIC_BASE_PATH}${newPathname}`
      window.location.replace(newUrl) // Forces a fresh load of the new path
    }

    router.events.on('routeChangeStart', reload) // Subscribe
    return () => router.events.off('routeChangeStart', reload) // Unsubscribe
  }, [latestBundleId, router.events])
}

// Get the top-level path, e.g. "/about/whatever" → "about"
const getTopLevelPath = (pathname: string) => pathname.split('/')[1] || ''

const useJsBundleInvalidation_development = () => {
  useEffect(() => {
    console.debug('Development mode: Ignoring JS bundle invalidation')
  }, [])
}

// Available when not in developement mode
const isAvailable = currentBundleId !== developmentBundleId

export const useJsBundleInvalidation = isAvailable ? useJsBundleInvalidation_production : useJsBundleInvalidation_development
